import { useMemo, useState, useCallback } from "react";
import {
  AvailableReturnItemReason,
  ReturnItemInput,
  useLanguage,
  ButtonData,
  ExtendedOrderItem,
} from "@narvar/nth-kit-returns-headless";
import { ReturnReasonsStepsProps } from "./types";
import { getSupportedTypes, ReturnReasonsFormDataValues } from "./formData";
import {
  ReturnReasonsReturnType,
  ReturnReasonsStep,
  ReturnReasonsPayload,
  ReturnReasonsDefaultType,
} from "./types";

export type ReturnReasonsPathHistory = {
  step: ReturnReasonsStep;
  stepIndex: number;
  payload: ReturnReasonsPayload;
};

export type ReturnReasonsPathData = {
  step: ReturnReasonsStep;
  options: {
    types?: ReturnReasonsReturnType[];
    reasons?: AvailableReturnItemReason[];
    totalQuantity?: number;
    pictures?: {
      minCount?: number;
      maxCount?: number;
      maxSize?: number;
    };
    consumerCommentsRequired?: boolean;
  };
  navigation: ButtonData[];
};

export type ReturnReasonsPathState = {
  path: ReturnReasonsPathData[];
  isLastStep: boolean;
  isCompleted: boolean;
  history: ReturnReasonsPathHistory[];
  next: (payload: ReturnReasonsPayload) => void;
  back: () => ReturnReasonsPathHistory;
};

export const useReturnReasonsPath: (options: {
  orderItem: ExtendedOrderItem;
  selectedItem?: ReturnItemInput;
  value: ReturnReasonsFormDataValues;
  returnQuantityMustBeAll?: boolean | null;
  skipExchangeSelection?: boolean;
  skipUpsellSelection?: boolean;
  stepsProps?: ReturnReasonsStepsProps;
  defaultType?: ReturnReasonsDefaultType;
}) => ReturnReasonsPathState = ({
  orderItem,
  selectedItem,
  value,
  returnQuantityMustBeAll = false,
  skipExchangeSelection,
  skipUpsellSelection,
  stepsProps,
  defaultType,
}) => {
  const {
    return: returnEnabled,
    exchange: exchangeEnabled,
    claim: claimEnabled,
    defaultType: suggestedDefaultType,
  } = getSupportedTypes({ orderItem, stepsProps });
  defaultType = defaultType ?? suggestedDefaultType;
  const backLabel = useLanguage({ langKey: "returnsActionsBack" }).value ?? "";
  const nextLabel = useLanguage({ langKey: "returnsActionsNext" }).value ?? "";
  const submitLabel = nextLabel;
  const noThanksLabel =
    useLanguage({
      langKey: "returnsReasonsExchangeUpsellNoThanks",
    }).value ?? "";

  // compute the steps / flow required in the return reasons form
  const steps = useMemo<ReturnReasonsStep[]>(() => {
    if (selectedItem) return ["editSelector"];

    const ret: ReturnReasonsStep[] = [];
    const isExchangeFlow =
      value?.type === "exchange" || value?.type === "exchangeForCredit";

    if (!defaultType) ret.push("typeSelector");
    if (isExchangeFlow) {
      if (!skipExchangeSelection) ret.push("exchangeItemSelector");

      if (stepsProps?.exchanges?.simpleFlow && orderItem.remainingQuantity > 1)
        ret.push("quantitySelector");

      // simple exchange flow would not ask for reasons, comments, etc
      if (stepsProps?.exchanges?.simpleFlow) return ret;
    }

    // remaining steps are return flow or exchange flow with details
    ret.push("reasonSelector");

    if (orderItem.remainingQuantity > 1) {
      if (
        returnQuantityMustBeAll &&
        orderItem.remainingQuantity === orderItem.orderedQuantity
      ) {
        ret.push("quantityAll");
      } else {
        ret.push("quantitySelector");
      }
    }

    if (stepsProps?.pictures?.enabled) ret.push("picturesUploader");

    // If the comments config is undefined OR set as allowed, we add the commentsForm as a step.
    if (
      !value?.selection?.[0]?.comments ||
      value?.selection?.[0]?.comments?.allowed
    ) {
      ret.push("commentsForm");
    }

    if (exchangeEnabled && !isExchangeFlow && !skipUpsellSelection)
      ret.push("upsellSelector");

    return ret;
  }, [
    orderItem,
    selectedItem,
    defaultType,
    value,
    exchangeEnabled,
    returnQuantityMustBeAll,
    skipExchangeSelection,
    skipUpsellSelection,
    stepsProps,
  ]);

  const [history, setHistory] = useState<ReturnReasonsPathHistory[]>([]);

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const currentStep: ReturnReasonsStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex >= steps.length - 1;
  const isCompleted = currentStepIndex >= steps.length;

  const path = useMemo<ReturnReasonsPathData[]>(
    () =>
      steps
        .filter((d, i) => i <= currentStepIndex)
        .map((step) => {
          const data: ReturnReasonsPathData = {
            step,
            options: {},
            navigation: [],
          };

          const nextButton: ButtonData = {
            type: isLastStep ? "submit" : "next",
            label: isLastStep ? submitLabel : nextLabel,
            disabled: true,
          };

          if (step !== "editSelector") {
            data.navigation.push({
              type: "back",
              label: backLabel,
              disabled: false,
              outline: true,
            });
          }

          switch (step) {
            case "typeSelector": {
              data.options.types = [];
              if (returnEnabled) data.options.types.push("return");
              if (exchangeEnabled) data.options.types.push("exchange");
              if (claimEnabled) data.options.types.push("claim");
              break;
            }
            case "reasonSelector": {
              data.options.reasons = orderItem.availableReasons ?? [];
              break;
            }
            case "quantityAll": {
              data.options.totalQuantity = orderItem.remainingQuantity;
              break;
            }
            case "quantitySelector": {
              data.options.totalQuantity = orderItem.remainingQuantity;
              break;
            }
            case "picturesUploader": {
              data.options.pictures = {
                minCount: stepsProps?.pictures?.enabled ? 1 : 0,
                maxCount: stepsProps?.pictures?.maxCount,
                maxSize: stepsProps?.pictures?.maxSize,
              };
              const maxCount =
                stepsProps?.pictures?.maxCount ?? Number.MAX_VALUE;
              const minCount = stepsProps?.pictures?.required ? 1 : 0;
              const count = value.pictures?.length ?? 0;
              data.navigation.push({
                ...nextButton,
                disabled: count < minCount || count > maxCount,
              });
              break;
            }
            case "commentsForm": {
              data.options.consumerCommentsRequired =
                stepsProps?.comments?.required;
              data.navigation.push({
                ...nextButton,
                disabled:
                  (!!stepsProps?.comments?.required &&
                    !value.customerComments) ||
                  (!!stepsProps?.comments?.attestation && !value.attested),
              });
              break;
            }
            case "exchangeItemSelector":
            case "upsellSelector": {
              const upsell = step === "upsellSelector";
              const skipExchange =
                upsell &&
                Object.entries(value.exchangeSelections ?? {}).length === 0;
              const notSelected =
                !value.exchangeTo || !value.exchangeTo?.available;
              data.navigation.push({
                type: isLastStep ? "submit" : "next",
                label: !isLastStep
                  ? nextLabel
                  : skipExchange
                    ? noThanksLabel
                    : submitLabel,
                disabled: !skipExchange && notSelected,
              });
              break;
            }
          }

          return data;
        }),
    [
      orderItem,
      value,
      returnEnabled,
      exchangeEnabled,
      claimEnabled,
      stepsProps,
      steps,
      currentStepIndex,
      isLastStep,
      backLabel,
      nextLabel,
      submitLabel,
      noThanksLabel,
    ],
  );

  const next = useCallback(
    (payload: ReturnReasonsPayload) => {
      let saveState = { ...payload };
      let nextStepIndex = currentStepIndex + 1;

      const _currentStep = ["quantityAll", "quantitySelector"].includes(
        currentStep,
      )
        ? "quantity"
        : currentStep;

      switch (_currentStep) {
        case "typeSelector": {
          // we don't need to remember the current type selection, so we won't
          // show a returning/exchanging tag in item preview
          saveState = {
            ...saveState,
            type: undefined,
          };
          break;
        }
        case "reasonSelector": {
          const reason = saveState.path[saveState.path.length - 1];
          const hasChild = (reason?.childIds?.length ?? 0) > 0;
          if (reason && hasChild) {
            nextStepIndex = currentStepIndex;
          }

          // we don't need to remember the current reason selection, otherwise
          // when user go back, it brings user to the child reason page.
          const newPath = saveState.path.slice(0, -1);
          const parent = newPath[newPath.length - 1];
          const grandparent = newPath[newPath.length - 2];
          saveState = {
            ...saveState,
            path: newPath,
            selectedReason: {
              id: parent?.id ?? "",
              name: parent?.name ?? "",
              category: parent?.name,
              parent: grandparent?.id,
            },
          };
          break;
        }
        case "quantity": {
          saveState = {
            ...saveState,
            quantity: undefined,
          };
          break;
        }
      }

      setHistory((h) => [
        ...h,
        { step: currentStep, stepIndex: currentStepIndex, payload: saveState },
      ]);
      setCurrentStepIndex(nextStepIndex);
    },
    [currentStep, currentStepIndex],
  );

  const back = useCallback(() => {
    const previousStep = history[history.length - 1];
    const updates = history.slice(0, -1);
    if (previousStep) {
      setHistory(updates);
      setCurrentStepIndex(previousStep?.stepIndex);
    }
    return previousStep;
  }, [history]);

  return useMemo(
    () => ({
      path,
      isLastStep,
      isCompleted,
      history,
      next,
      back,
    }),
    [path, isLastStep, isCompleted, history, next, back],
  );
};
